import React from 'react';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import Icons from '../Icons';
import { formatDate, getRiskColor, getRiskLabel, getRiskVariant } from './safeguardingUtils';

const categoryIcons = {
  'Social Care': Icons.Shield,
  Safeguarding: Icons.Shield,
  Medical: Icons.Document,
  Police: Icons.Alert
};

const statusConfig = {
  open: { label: 'Open', icon: Icons.Alert, variant: 'danger' },
  monitoring: { label: 'Monitoring', icon: Icons.Clock, variant: 'warning' },
  closed: { label: 'Closed', icon: Icons.Check, variant: 'inactive' },
  resolved: { label: 'Closed', icon: Icons.Check, variant: 'inactive' }
};

export default function SafeguardingEventCard({
  event,
  onReview,
  onAssignOwner,
  onSetReviewDate,
  muted = false
}) {
  if (!event) return null;
  const riskLabel = getRiskLabel(event.severity);
  const riskVariant = getRiskVariant(event.severity);
  const statusKey = (event.status || '').toLowerCase();
  const status = statusConfig[statusKey] || statusConfig.open;
  const StatusIcon = status.icon;
  const Icon = categoryIcons[event.category] || Icons.Shield;
  const missingOwner = !event.assignedOwner;
  const missingReview = !event.reviewDate;

  return (
    <article
      className={`sp-panel sp-event-card p-4 ${muted ? 'sp-event-card--muted' : ''}`}
      style={{ borderLeft: `4px solid ${getRiskColor(event.severity)}` }}
      aria-label={`${event.type || event.category} safeguarding event`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="mt-1 text-slate-600" aria-hidden="true">
            <Icon />
          </div>
          <div>
            <div className="sp-event-kicker">{event.category}</div>
            <h4 className="sp-event-title">
              {event.type || event.category || 'Safeguarding event'} —{' '}
              <span className={`sp-risk-text sp-risk-text--${riskVariant}`}>{riskLabel}</span> — {status.label}
            </h4>
            {event.description && (
              <p className="text-sm sp-muted mt-2">{event.description}</p>
            )}
            <div className="sp-event-tags">
              <Badge variant={status.variant}>
                <span aria-hidden="true">
                  <StatusIcon />
                </span>
                {status.label}
              </Badge>
              {missingOwner && <Badge variant="danger">Unassigned owner</Badge>}
              {missingReview && <Badge variant="warning">No review date</Badge>}
            </div>
            <div className="sp-event-meta">
              <span>
                <span className="sp-meta-label">Owner</span>
                <span className={missingOwner ? 'sp-warning-text' : ''}>{event.assignedOwner || 'Unassigned'}</span>
              </span>
              <span>
                <span className="sp-meta-label">Date</span>
                <span>{formatDate(event.date) || 'No date'}</span>
              </span>
              <span>
                <span className="sp-meta-label">Review</span>
                <span className={missingReview ? 'sp-warning-text' : ''}>{formatDate(event.reviewDate) || 'Not scheduled'}</span>
              </span>
              <span>
                <span className="sp-meta-label">Escalation</span>
                <span>{event.escalationLevel || 'None'}</span>
              </span>
              <span>
                <span className="sp-meta-label">Attachments</span>
                <span>{event.attachments?.length || 0}</span>
              </span>
            </div>
          </div>
        </div>
        <div className="sp-actions">
          <Button
            size="sm"
            variant="ghost"
            type="button"
            onClick={() => onReview?.(event)}
            aria-label={`Review ${event.type || event.id}`}
          >
            Review / Update
          </Button>
          {missingOwner && (
            <Button
              size="sm"
              variant="secondary"
              type="button"
              onClick={() => onAssignOwner?.(event)}
              aria-label={`Assign owner for ${event.type || event.id}`}
            >
              Assign owner
            </Button>
          )}
          {missingReview && (
            <Button
              size="sm"
              variant="secondary"
              type="button"
              onClick={() => onSetReviewDate?.(event)}
              aria-label={`Set review date for ${event.type || event.id}`}
            >
              Set review date
            </Button>
          )}
        </div>
      </div>
    </article>
  );
}
